import React, { useState, useEffect } from "react";
import { withAsyncPaginate } from "react-select-async-paginate";
import Creatable from "react-select/creatable";
import AsyncCreatable from "react-select/async-creatable";
import { supabase } from "../supabaseClient";


// Map field names to their database tables
import { Modal } from "./Modal/index.jsx";

const TABLE_CONFIG = {
  genre: {
    table: "genres",
    nameField: "genres",
    labelField: "genres",
    setOptions: "setGenreOptions",
    idField: "id",
  },
  languages: {
    table: "languages",
    nameField: "languages",
    labelField: "languages",
    setOptions: "setLanguagesOptions",
    idField: "id",
  },
  cast: {
    table: "castMembers",
    nameField: "castMembers",
    labelField: "castMembers",
    setOptions: "setCastMembersOptions",
    idField: "id",
  },
  director: {
    table: "directors",
    nameField: "directors",
    labelField: "directors",
    setOptions: "setDirectorsOptions",
    idField: "id",
  },
  contentType: {
    table: "contentType",
    nameField: "contentType",
    labelField: "contentType",
    setOptions: "setContenttypeOptions",
    idField: "id",
  },
  source: {
    table: "source",
    nameField: "source",
    labelField: "source",
    setOptions: "setSourceOptions",
    idField: "id",
  },
};

const ConfirmModal = ({ show, onClose, onConfirm, pendingValue }) => {
  if (!show) return null;

  return (
    <Modal showModal={show} setShowModal={onClose}>
      <div className="p-6">
        <h2 className="text-lg font-semibold mb-4">Confirm Create</h2>
        <p className="mb-6 text-gray-600">
          Are you sure you want to add "<strong>{pendingValue}</strong>"?
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Yes, Add It
          </button>
        </div>
      </div>
    </Modal>
  );
};

const CreatableMultiSelect = ({
  field, // 'genre', 'languages', 'cast', 'director', 'contentType'
  options = [],
  value = [],
  onChange,
  placeholder = "Select or create...",
  setOptionsCallback, // Callback to update parent state with new options
  loadOptions, // Optional async loadOptions for search/pagination
  additional = { page: 1 }, // For AsyncPaginate
}) => {
  const selectRef = React.useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingInputValue, setPendingInputValue] = useState("");


  // Determine if async mode (loadOptions provided)
  const isAsync = !!loadOptions;

  // Async Creatable with pagination
  const AsyncCreatableSelect = withAsyncPaginate(Creatable);

  // Handle create confirmation
  const handleCreateTrigger = (inputValue) => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;

    setPendingInputValue(trimmedValue);
    setShowConfirm(true);
  };

  const handleConfirmYes = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    performCreate().finally(() => {
      // Blur select input to prevent Enter key form submit
      if (selectRef.current) {
        selectRef.current.blur();
      }
      setInputValue("");
    });
  };


  // Perform the actual DB create logic
  const performCreate = async () => {
    const config = TABLE_CONFIG[field];
    if (!config) {
      console.error(`Unknown field: ${field}`);
      return;
    }

    try {
      const { data: existing, error: searchError } = await supabase
        .from(config.table)
        .select(`${config.idField}, ${config.nameField}`)
        .eq(config.nameField, pendingInputValue)
        .maybeSingle();

      if (searchError) {
        console.error(`Error searching ${config.table}:`, searchError);
        return;
      }

      let newOption;

      if (existing) {
        newOption = {
          label: existing[config.nameField],
          value: existing[config.idField],
        };
      } else {
        const { data: newRecord, error: insertError } = await supabase
          .from(config.table)
          .insert([{ [config.nameField]: pendingInputValue }])
          .select()
          .single();

        if (insertError) {
          console.error(`Error inserting into ${config.table}:`, insertError);
          return;
        }

        newOption = {
          label: newRecord[config.nameField],
          value: newRecord[config.idField],
        };
      }

      if (setOptionsCallback && !isAsync) {
        const newOptions = [...options, newOption];
        setOptionsCallback(newOptions);
      }

      const newValue = [...value, newOption];
      onChange(newValue);

      return newOption;
    } catch (err) {
      console.error("Error in performCreate:", err);
    } finally {
      setShowConfirm(false);
      setPendingInputValue("");
    }
  };

  const handleConfirmNo = () => {
    setShowConfirm(false);
    setPendingInputValue("");
  };

  // Wrapper for react-select onCreateOption
  const handleCreateOption = (inputValue) => {
    handleCreateTrigger(inputValue);
  }; 

  // Handle changes from the select
  const handleChange = (selectedOptions) => {
    onChange(selectedOptions || []);
  };
 const isUUID = (val) =>
  typeof val === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);

  useEffect(() => {
  const fetchMissingOptions = async () => {
    if (!value || value.length === 0) return;

    const config = TABLE_CONFIG[field];
    if (!config) return;

    // extract ids (value can be object or primitive)
    // const ids = value.map(v => (typeof v === "object" ? v.value : v));
    const ids = value
  .map(v => (typeof v === "object" ? v.value : v))
  .filter(id => isUUID(id)); // 🔥 ONLY UUIDs

    // check which ids are missing in current options
    const existingIds = new Set(options.map(opt => opt.value));
    const missingIds = ids.filter(id => !existingIds.has(id));

    if (missingIds.length === 0) return;

    const { data, error } = await supabase
      .from(config.table)
      .select(`${config.idField}, ${config.nameField}`)
      .in(config.idField, missingIds);

    if (error) {
      console.error("Prefetch error:", error);
      return;
    }

    const fetchedOptions = data.map(item => ({
      label: item[config.nameField],
      value: item[config.idField],
    }));

    // merge with existing options
    if (setOptionsCallback) {
      setOptionsCallback(prev => {
        const merged = [...prev, ...fetchedOptions];

        // remove duplicates
        const map = new Map();
        merged.forEach(opt => map.set(opt.value, opt));
        return Array.from(map.values());
      });
    }
  };

  fetchMissingOptions();
}, [value]);

  // Use AsyncCreatable if loadOptions provided
  if (isAsync) {
    return (
      <>
        <AsyncCreatableSelect
          ref={selectRef}
          isMulti
          loadOptions={loadOptions}
          additional={additional}
          value={value}
          onChange={handleChange}
          onCreateOption={handleCreateOption}
          placeholder={placeholder}
          formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
          createOptionPosition="first"
          className="w-full"
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
        <ConfirmModal
          show={showConfirm}
          onClose={handleConfirmNo}
          onConfirm={handleConfirmYes}
          pendingValue={pendingInputValue}
        />
      </>
    );
  } 

  // Fallback to regular Creatable for static options
  return (
    <>
      <Creatable
        ref={selectRef}
        isMulti
        options={options}
        value={value}
        onChange={handleChange}
        onCreateOption={handleCreateOption}
        placeholder={placeholder}
        formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
        createOptionPosition="first"
        className="w-full"
        classNamePrefix="react-select"
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
      />
      <ConfirmModal
        show={showConfirm}
        onClose={handleConfirmNo}
        onConfirm={handleConfirmYes}
        pendingValue={pendingInputValue}
      />
    </>
  );
};



export default CreatableMultiSelect;

